import {ReactNode, useCallback, useEffect, useMemo, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {useNavigate} from 'react-router-dom';
import {mdiArrowRight} from '@mdi/js';
import {Formik, FormikHelpers} from 'formik';
import orderBy from 'lodash/orderBy';
import {Area, AreaChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis} from 'recharts';

import {
  acceptConnectFiveChallenge,
  cancelConnectFiveChallenge,
  createConnectFiveChallenge,
  getConnectFiveChallenges,
  getConnectFiveEloSnapshots,
  getConnectFiveLeaderboard,
  getConnectFiveMatches,
} from 'api/connectFive';
import Avatar from 'components/Avatar';
import {BadgeStyle} from 'components/Badge';
import Button, {ButtonColor, ButtonType} from 'components/Button';
import EmptyText from 'components/EmptyText';
import {FormField} from 'components/FormElements';
import Loader from 'components/Loader';
import UserLabel from 'components/UserLabel';
import {ConnectFiveChallengeStatus, ConnectFiveMatchStatus} from 'enums';
import {
  getConnectFiveActiveMatches,
  getConnectFiveCompletedMatches,
  getConnectFiveIncomingChallenges,
  getConnectFiveOutgoingChallenges,
  getSelf,
} from 'selectors/state';
import {
  removeIncomingChallenge,
  setActiveMatches,
  setCompletedMatches,
  setIncomingChallenges,
  setOutgoingChallenges,
  upsertChallenge,
  upsertMatch,
} from 'store/connectFive';
import {colors, radii, shadows} from 'styles';
import {
  AppDispatch,
  ConnectFiveChallenge,
  ConnectFiveEloSnapshot,
  ConnectFiveLeaderboardEntry,
  ConnectFiveMatch,
  SelectOption,
  SFC,
  UserReadSerializer,
} from 'types';
import {shortDate} from 'utils/dates';
import {handleFormikAPIError} from 'utils/forms';
import {displayErrorToast} from 'utils/toasts';
import yup from 'utils/yup';

import * as S from './Styles';

const timeLimitOptions: SelectOption[] = [
  {displayName: '5 minutes', value: '300'},
  {displayName: '10 minutes', value: '600'},
  {displayName: '15 minutes', value: '900'},
  {displayName: '30 minutes', value: '1800'},
];

const MATCHES_PER_PAGE = 10;

const initialValues = {
  maxSpendAmount: 0,
  opponent: null as UserReadSerializer | null,
  stakeAmount: '',
  timeLimitSeconds: '600',
};

type FormValues = typeof initialValues;

const getChallengeStatusBadge = (status: string) => {
  const label = status.replace('_', ' ');
  const formattedLabel = label.charAt(0).toUpperCase() + label.slice(1);

  if (status === ConnectFiveChallengeStatus.PENDING) {
    return {badgeStyle: BadgeStyle.primary, label: 'Pending'};
  }

  if (status === ConnectFiveChallengeStatus.ACCEPTED) {
    return {badgeStyle: BadgeStyle.success, label: 'Accepted'};
  }

  if (status === ConnectFiveChallengeStatus.CANCELLED) {
    return {badgeStyle: BadgeStyle.warning, label: 'Cancelled'};
  }

  if (status === ConnectFiveChallengeStatus.EXPIRED) {
    return {badgeStyle: BadgeStyle.danger, label: 'Expired'};
  }

  return {badgeStyle: BadgeStyle.neutral, label: formattedLabel};
};

const getFinishReasonLabel = (match: ConnectFiveMatch): string | null => {
  if (match.status === ConnectFiveMatchStatus.FINISHED_CONNECT5) {
    return 'Connect 5';
  }

  if (match.status === ConnectFiveMatchStatus.FINISHED_FULL_BOARD) {
    return 'Full board';
  }

  if (match.status === ConnectFiveMatchStatus.FINISHED_RESIGN) {
    return 'Resignation';
  }

  if (match.status === ConnectFiveMatchStatus.FINISHED_TIMEOUT) {
    return 'Timeout';
  }

  return null;
};

const getOpponent = (match: ConnectFiveMatch, selfId?: number | null): UserReadSerializer | null => {
  if (selfId) {
    if (match.player_a.id === selfId) return match.player_b;
    if (match.player_b.id === selfId) return match.player_a;
  }

  return match.player_b ?? match.player_a;
};

const getStatusBadge = (match: ConnectFiveMatch, selfId?: number | null) => {
  if (match.status === ConnectFiveMatchStatus.ACTIVE) {
    return {badgeStyle: BadgeStyle.primary, label: 'In progress'};
  }

  if (match.status === ConnectFiveMatchStatus.CANCELLED) {
    return {badgeStyle: BadgeStyle.warning, label: 'Cancelled'};
  }

  if (match.winner && selfId) {
    if (match.winner === selfId) {
      return {badgeStyle: BadgeStyle.success, label: 'You won!'};
    }

    return {badgeStyle: BadgeStyle.danger, label: 'You lost'};
  }

  return {badgeStyle: BadgeStyle.neutral, label: 'Finished'};
};

const isMatchParticipant = (match: ConnectFiveMatch, userId?: number | null): boolean => {
  if (!userId) return false;
  return match.player_a.id === userId || match.player_b.id === userId;
};

const ConnectFiveHome: SFC = ({className}) => {
  const [activeMatchesPage, setActiveMatchesPage] = useState(1);
  const [completedMatchesPage, setCompletedMatchesPage] = useState(1);
  const [eloError, setEloError] = useState<string | null>(null);
  const [eloSnapshots, setEloSnapshots] = useState<ConnectFiveEloSnapshot[]>([]);
  const [isEloLoading, setIsEloLoading] = useState(true);
  const [isLeaderboardLoading, setIsLeaderboardLoading] = useState(true);
  const [isMatchesLoading, setIsMatchesLoading] = useState(true);
  const [publicMatches, setPublicMatches] = useState<ConnectFiveMatch[]>([]);
  const [publicMatchesPage, setPublicMatchesPage] = useState(1);
  const [selfLeaderboardEntry, setSelfLeaderboardEntry] = useState<ConnectFiveLeaderboardEntry | null>(null);
  const [selfLeaderboardRank, setSelfLeaderboardRank] = useState<number | null>(null);

  const activeMatches = useSelector(getConnectFiveActiveMatches);
  const completedMatches = useSelector(getConnectFiveCompletedMatches);
  const dispatch = useDispatch<AppDispatch>();
  const incomingChallenges = useSelector(getConnectFiveIncomingChallenges);
  const navigate = useNavigate();
  const outgoingChallenges = useSelector(getConnectFiveOutgoingChallenges);
  const self = useSelector(getSelf);

  const activeMatchesSorted = useMemo(() => {
    return orderBy(activeMatches, ['created_date'], ['desc']);
  }, [activeMatches]);

  const activeMatchesTotalPages = useMemo(() => {
    return Math.ceil(activeMatchesSorted.length / MATCHES_PER_PAGE);
  }, [activeMatchesSorted.length]);

  const completedMatchesSorted = useMemo(() => {
    return orderBy(completedMatches, ['created_date'], ['desc']);
  }, [completedMatches]);

  const completedMatchesTotalPages = useMemo(() => {
    return Math.ceil(completedMatchesSorted.length / MATCHES_PER_PAGE);
  }, [completedMatchesSorted.length]);
  const hasMoreCompletedMatches = completedMatchesPage < completedMatchesTotalPages;

  const selfMatchRecord = useMemo(() => {
    if (!self?.id) {
      return {losses: 0, wins: 0};
    }

    return completedMatches.reduce(
      (record, match) => {
        if (!match.winner) return record;
        if (match.winner === self.id) {
          record.wins += 1;
          return record;
        }

        record.losses += 1;
        return record;
      },
      {losses: 0, wins: 0},
    );
  }, [completedMatches, self?.id]);

  const eloChartData = useMemo(() => {
    return orderBy(eloSnapshots, ['date'], ['asc']);
  }, [eloSnapshots]);

  const publicMatchesSorted = useMemo(() => {
    return orderBy(publicMatches, ['created_date'], ['desc']);
  }, [publicMatches]);

  const publicMatchesTotalPages = useMemo(() => {
    return Math.ceil(publicMatchesSorted.length / MATCHES_PER_PAGE);
  }, [publicMatchesSorted.length]);

  const paginatedActiveMatches = useMemo(() => {
    const startIndex = (activeMatchesPage - 1) * MATCHES_PER_PAGE;
    return activeMatchesSorted.slice(startIndex, startIndex + MATCHES_PER_PAGE);
  }, [activeMatchesPage, activeMatchesSorted]);

  const visibleCompletedMatches = useMemo(() => {
    return completedMatchesSorted.slice(0, completedMatchesPage * MATCHES_PER_PAGE);
  }, [completedMatchesPage, completedMatchesSorted]);

  const paginatedPublicMatches = useMemo(() => {
    const startIndex = (publicMatchesPage - 1) * MATCHES_PER_PAGE;
    return publicMatchesSorted.slice(startIndex, startIndex + MATCHES_PER_PAGE);
  }, [publicMatchesPage, publicMatchesSorted]);

  const validationSchema = useMemo(() => {
    return yup.object().shape({
      maxSpendAmount: yup.number().integer('Enter a whole number').min(0).required('Max spend is required'),
      opponent: yup
        .mixed()
        .required('Opponent is required')
        .test('not-self', "You can't challenge yourself", (value) => {
          if (!value || !self?.id) return true;
          return (value as UserReadSerializer).id !== self.id;
        }),
      stakeAmount: yup.number().integer('Enter a whole number').min(0).required('Stake is required'),
      timeLimitSeconds: yup.string().required('Time limit is required'),
    });
  }, [self?.id]);

  const formatEloDate = (value: string, options: Intl.DateTimeFormatOptions) => {
    const [year, month, day] = value.split('-').map(Number);
    const date = new Date(year, month - 1, day);
    return date.toLocaleDateString(undefined, options);
  };

  const formatEloDateLabel = (value: string) => {
    return formatEloDate(value, {day: 'numeric', month: 'short'});
  };

  const formatEloTooltipLabel = (value: string) => {
    return formatEloDate(value, {day: 'numeric', month: 'short', year: 'numeric'});
  };

  const formatEloValue = (value: number) => {
    return value.toLocaleString();
  };

  const handleAcceptChallenge = useCallback(
    async (challengeId: number) => {
      const match = await acceptConnectFiveChallenge(challengeId);
      dispatch(removeIncomingChallenge(challengeId));
      dispatch(upsertMatch({match, selfId: self?.id}));
      navigate(`/connect-five/matches/${match.id}`);
    },
    [dispatch, navigate, self?.id],
  );

  const handleActiveMatchesPageChange = useCallback((page: number) => {
    setActiveMatchesPage(page);
  }, []);

  const handleCancelChallenge = useCallback(
    async (challengeId: number) => {
      const challenge = await cancelConnectFiveChallenge(challengeId);
      if (self?.id) {
        dispatch(upsertChallenge({challenge, selfId: self.id}));
      }
    },
    [dispatch, self?.id],
  );

  const handleChallengeSubmit = useCallback(
    async (values: FormValues, helpers: FormikHelpers<FormValues>) => {
      try {
        if (!values.opponent) {
          helpers.setFieldError('opponent', 'Opponent is required');
          return;
        }

        const payload = {
          max_spend_amount: Number(values.maxSpendAmount),
          opponent_id: values.opponent.id,
          stake_amount: Number(values.stakeAmount),
          time_limit_seconds: Number(values.timeLimitSeconds),
        };

        const challenge = await createConnectFiveChallenge(payload);
        if (self?.id) {
          dispatch(upsertChallenge({challenge, selfId: self.id}));
        }
        navigate(`/connect-five/challenges/${challenge.id}`);
        helpers.resetForm();
      } catch (error) {
        handleFormikAPIError(error, helpers, 'Error creating challenge');
      }
    },
    [dispatch, navigate, self?.id],
  );

  const handleCompletedMatchesLoadMore = useCallback(() => {
    setCompletedMatchesPage((page) => (page < completedMatchesTotalPages ? page + 1 : page));
  }, [completedMatchesTotalPages]);

  const handleMatchCardClick = useCallback(
    (matchId: number) => {
      navigate(`/connect-five/matches/${matchId}`);
    },
    [navigate],
  );

  const handleOpponentChange = useCallback(
    (
      user: UserReadSerializer | null,
      setFieldTouched: FormikHelpers<FormValues>['setFieldTouched'],
      setFieldValue: FormikHelpers<FormValues>['setFieldValue'],
    ) => {
      setFieldValue('opponent', user);
      setFieldTouched('opponent', true, false);
    },
    [],
  );

  const handlePublicMatchesPageChange = useCallback((page: number) => {
    setPublicMatchesPage(page);
  }, []);

  const loadChallenges = useCallback(async () => {
    const incomingResponse = await getConnectFiveChallenges({
      mine: 'received',
      status: ConnectFiveChallengeStatus.PENDING,
    });
    const outgoingResponse = await getConnectFiveChallenges({
      mine: 'sent',
      status: ConnectFiveChallengeStatus.PENDING,
    });

    dispatch(setIncomingChallenges(incomingResponse.results));
    dispatch(setOutgoingChallenges(outgoingResponse.results));
  }, [dispatch]);

  const loadEloSnapshots = useCallback(async () => {
    try {
      setEloError(null);
      setIsEloLoading(true);
      const response = await getConnectFiveEloSnapshots();
      setEloSnapshots(response);
    } catch (error) {
      displayErrorToast('Unable to load ELO history.');
      setEloError('Unable to load ELO history.');
    } finally {
      setIsEloLoading(false);
    }
  }, []);

  const loadLeaderboardEntry = useCallback(async () => {
    if (!self?.id) {
      setSelfLeaderboardEntry(null);
      setSelfLeaderboardRank(null);
      setIsLeaderboardLoading(false);
      return;
    }

    setIsLeaderboardLoading(true);

    try {
      const fetchLeaderboardEntry = async (
        url: string | null,
        entriesProcessed: number,
      ): Promise<{entry: ConnectFiveLeaderboardEntry; rank: number} | null> => {
        const response = url ? await getConnectFiveLeaderboard({url}) : await getConnectFiveLeaderboard();
        const entryIndex = response.results.findIndex((entry) => entry.user.id === self.id);

        if (entryIndex !== -1) {
          return {
            entry: response.results[entryIndex],
            rank: entriesProcessed + entryIndex + 1,
          };
        }

        if (!response.next) {
          return null;
        }

        return fetchLeaderboardEntry(response.next, entriesProcessed + response.results.length);
      };

      const result = await fetchLeaderboardEntry(null, 0);

      if (!result) {
        setSelfLeaderboardEntry(null);
        setSelfLeaderboardRank(null);
        return;
      }

      setSelfLeaderboardEntry(result.entry);
      setSelfLeaderboardRank(result.rank);
    } catch (error) {
      displayErrorToast('Unable to load leaderboard info.');
    } finally {
      setIsLeaderboardLoading(false);
    }
  }, [self?.id]);

  const loadMatches = useCallback(async () => {
    setIsMatchesLoading(true);

    try {
      const [selfMatchesResponse, publicMatchesResponse] = await Promise.all([
        getConnectFiveMatches({mine: 'self'}),
        getConnectFiveMatches({mine: 'exclude', status: ConnectFiveMatchStatus.ACTIVE}),
      ]);
      const selfMatches = selfMatchesResponse.results;
      const filteredPublicMatches = self?.id
        ? publicMatchesResponse.results.filter((match) => !isMatchParticipant(match, self.id))
        : publicMatchesResponse.results;

      dispatch(setActiveMatches(selfMatches.filter((match) => match.status === ConnectFiveMatchStatus.ACTIVE)));
      dispatch(setCompletedMatches(selfMatches.filter((match) => match.status !== ConnectFiveMatchStatus.ACTIVE)));
      setPublicMatches(filteredPublicMatches);
    } finally {
      setIsMatchesLoading(false);
    }
  }, [dispatch, self?.id]);

  useEffect(() => {
    const loadData = async () => {
      await Promise.all([loadChallenges(), loadMatches()]);
    };

    loadData();
  }, [loadChallenges, loadMatches]);

  useEffect(() => {
    loadEloSnapshots();
  }, [loadEloSnapshots]);

  useEffect(() => {
    loadLeaderboardEntry();
  }, [loadLeaderboardEntry]);

  useEffect(() => {
    if (activeMatchesTotalPages && activeMatchesPage > activeMatchesTotalPages) {
      setActiveMatchesPage(activeMatchesTotalPages);
    } else if (!activeMatchesTotalPages && activeMatchesPage !== 1) {
      setActiveMatchesPage(1);
    }

    if (completedMatchesTotalPages && completedMatchesPage > completedMatchesTotalPages) {
      setCompletedMatchesPage(completedMatchesTotalPages);
    } else if (!completedMatchesTotalPages && completedMatchesPage !== 1) {
      setCompletedMatchesPage(1);
    }
    if (publicMatchesTotalPages && publicMatchesPage > publicMatchesTotalPages) {
      setPublicMatchesPage(publicMatchesTotalPages);
    } else if (!publicMatchesTotalPages && publicMatchesPage !== 1) {
      setPublicMatchesPage(1);
    }
  }, [
    activeMatchesPage,
    activeMatchesTotalPages,
    completedMatchesPage,
    completedMatchesTotalPages,
    publicMatchesPage,
    publicMatchesTotalPages,
  ]);

  const isRecordLoading = isLeaderboardLoading || (!selfLeaderboardEntry && isMatchesLoading);
  const rankLabel = (() => {
    if (isLeaderboardLoading) {
      return 'Rank -';
    }

    if (selfLeaderboardRank) {
      return `Rank #${selfLeaderboardRank}`;
    }

    return 'Rank: Unranked';
  })();
  const recordLabel = (() => {
    if (isRecordLoading) {
      return '-';
    }

    if (selfLeaderboardEntry) {
      return `${selfLeaderboardEntry.wins}W - ${selfLeaderboardEntry.losses}L`;
    }

    return `${selfMatchRecord.wins}W - ${selfMatchRecord.losses}L`;
  })();

  const renderEloChart = () => {
    if (isEloLoading) {
      return (
        <S.EloChartBody>
          <Loader />
        </S.EloChartBody>
      );
    }

    if (eloError) {
      return (
        <S.EloChartBody>
          <EmptyText>{eloError}</EmptyText>
        </S.EloChartBody>
      );
    }

    if (!eloChartData.length) {
      return (
        <S.EloChartBody>
          <EmptyText>No ELO history yet.</EmptyText>
        </S.EloChartBody>
      );
    }

    return (
      <S.EloChartBody>
        <S.EloChartWrapper>
          <ResponsiveContainer height="100%" width="100%">
            <AreaChart data={eloChartData} margin={{bottom: 0, left: 0, right: 8, top: 8}}>
              <CartesianGrid stroke={colors.palette.gray[200]} vertical={false} />
              <XAxis
                axisLine={{stroke: colors.palette.gray[300]}}
                dataKey="date"
                minTickGap={24}
                tick={{fill: colors.secondary, fontSize: 12}}
                tickFormatter={formatEloDateLabel}
                tickLine={false}
                tickMargin={8}
              />
              <YAxis
                axisLine={false}
                tick={{fill: colors.secondary, fontSize: 12}}
                tickFormatter={formatEloValue}
                tickLine={false}
                width={48}
              />
              <Tooltip
                contentStyle={{
                  backgroundColor: colors.white,
                  border: `1px solid ${colors.borderSubtle}`,
                  borderRadius: radii.small,
                  boxShadow: shadows.popover,
                  fontSize: 13,
                }}
                cursor={{stroke: colors.palette.gray[400]}}
                formatter={(value) => formatEloValue(Number(value))}
                itemStyle={{color: colors.primary}}
                labelFormatter={formatEloTooltipLabel}
                labelStyle={{color: colors.secondary}}
              />
              <Area
                activeDot={{fill: colors.palette.blue[500], r: 4, stroke: colors.white, strokeWidth: 2}}
                dataKey="elo"
                fill={colors.palette.blue[200]}
                fillOpacity={0.35}
                stroke={colors.palette.blue[500]}
                strokeWidth={2}
                type="monotone"
              />
            </AreaChart>
          </ResponsiveContainer>
        </S.EloChartWrapper>
      </S.EloChartBody>
    );
  };

  const renderYourMatches = () => {
    return (
      <>
        <S.MatchList>{paginatedActiveMatches.map(renderMatchCard)}</S.MatchList>
        <S.Pagination
          currentPage={activeMatchesPage}
          onPageChange={handleActiveMatchesPageChange}
          totalPages={activeMatchesTotalPages}
        />
      </>
    );
  };

  const renderChallengeCard = (
    challenge: ConnectFiveChallenge,
    opponent: UserReadSerializer | null,
    actions?: ReactNode,
  ) => {
    const createdLabel = shortDate(challenge.created_date, true);
    const statusBadge = getChallengeStatusBadge(challenge.status);

    return (
      <S.ChallengeCard key={challenge.id}>
        <S.ChallengeHeader>
          <UserLabel
            avatar={opponent?.avatar ?? null}
            clickable={false}
            description={createdLabel}
            id={opponent?.id ?? null}
            username={opponent?.username ?? 'Unknown player'}
          />
          <S.Badge badgeStyle={statusBadge.badgeStyle}>{statusBadge.label}</S.Badge>
        </S.ChallengeHeader>
        <S.ChallengeInfo>
          <S.ChallengeInfoRow>
            <S.ChallengeInfoLabel>Stake</S.ChallengeInfoLabel>
            <S.ChallengeInfoValue>{challenge.stake_amount.toLocaleString()} TNB</S.ChallengeInfoValue>
          </S.ChallengeInfoRow>
          <S.ChallengeInfoRow>
            <S.ChallengeInfoLabel>Max spend</S.ChallengeInfoLabel>
            <S.ChallengeInfoValue>{challenge.max_spend_amount.toLocaleString()} TNB</S.ChallengeInfoValue>
          </S.ChallengeInfoRow>
          <S.ChallengeInfoRow>
            <S.ChallengeInfoLabel>Time limit</S.ChallengeInfoLabel>
            <S.ChallengeInfoValue>{Math.round(challenge.time_limit_seconds / 60)} min</S.ChallengeInfoValue>
          </S.ChallengeInfoRow>
        </S.ChallengeInfo>
        {actions && <S.ChallengeActions>{actions}</S.ChallengeActions>}
      </S.ChallengeCard>
    );
  };

  const renderCompletedMatches = () => {
    return (
      <>
        <S.MatchList>{visibleCompletedMatches.map(renderMatchCard)}</S.MatchList>
        {hasMoreCompletedMatches && (
          <S.LoadMoreRow>
            <Button color={ButtonColor.secondary} onClick={handleCompletedMatchesLoadMore} text="Load more" />
          </S.LoadMoreRow>
        )}
      </>
    );
  };

  const renderPublicMatches = () => {
    return (
      <>
        <S.MatchList>{paginatedPublicMatches.map(renderPublicMatchCard)}</S.MatchList>
        <S.Pagination
          currentPage={publicMatchesPage}
          onPageChange={handlePublicMatchesPageChange}
          totalPages={publicMatchesTotalPages}
        />
      </>
    );
  };

  const renderIncomingChallenges = () => {
    return (
      <S.ChallengeList>
        {incomingChallenges.map((challenge) =>
          renderChallengeCard(
            challenge,
            challenge.challenger,
            <Button onClick={() => handleAcceptChallenge(challenge.id)} text="Accept" />,
          ),
        )}
      </S.ChallengeList>
    );
  };

  const renderMatchCard = (match: ConnectFiveMatch) => {
    const createdLabel = shortDate(match.created_date, true);
    const finishReason = getFinishReasonLabel(match);
    const isActive = match.status === ConnectFiveMatchStatus.ACTIVE;
    const opponent = getOpponent(match, self?.id);
    const statusBadge = getStatusBadge(match, self?.id);

    return (
      <S.MatchCard
        aria-label={`Open match ${match.id}`}
        key={match.id}
        onClick={() => handleMatchCardClick(match.id)}
        type="button"
      >
        <S.MatchHeader>
          <S.MatchHeaderMain>
            <UserLabel
              avatar={opponent?.avatar ?? null}
              clickable={false}
              description={createdLabel}
              id={opponent?.id ?? null}
              username={opponent?.username ?? 'Unknown player'}
            />
          </S.MatchHeaderMain>
          <S.MatchIcon path={mdiArrowRight} size="20px" />
        </S.MatchHeader>
        <S.MatchInfo>
          <S.MatchInfoRow>
            <S.MatchInfoLabel>Status</S.MatchInfoLabel>
            <S.Badge badgeStyle={statusBadge.badgeStyle}>{statusBadge.label}</S.Badge>
          </S.MatchInfoRow>
          {finishReason && (
            <S.MatchInfoRow>
              <S.MatchInfoLabel>Finish reason</S.MatchInfoLabel>
              <S.MatchInfoValue>{finishReason}</S.MatchInfoValue>
            </S.MatchInfoRow>
          )}
          {!isActive && match.winner && (
            <S.MatchInfoRow>
              <S.MatchInfoLabel>Winner</S.MatchInfoLabel>
              <S.MatchInfoValue>
                {match.winner === match.player_a.id ? match.player_a.username : match.player_b.username}
              </S.MatchInfoValue>
            </S.MatchInfoRow>
          )}
        </S.MatchInfo>
      </S.MatchCard>
    );
  };

  const renderPublicMatchCard = (match: ConnectFiveMatch) => {
    const createdLabel = shortDate(match.created_date, true);
    const finishReason = getFinishReasonLabel(match);
    const isActive = match.status === ConnectFiveMatchStatus.ACTIVE;
    const statusBadge = getStatusBadge(match);

    return (
      <S.MatchCard
        aria-label={`Open match ${match.id}`}
        key={match.id}
        onClick={() => handleMatchCardClick(match.id)}
        type="button"
      >
        <S.MatchHeader>
          <S.MatchHeaderMain>
            <S.PublicMatchPlayers>
              <S.PublicMatchPlayer>
                <Avatar size="36px" src={match.player_a.avatar} />
                <S.PublicMatchPlayerName>{match.player_a.username}</S.PublicMatchPlayerName>
              </S.PublicMatchPlayer>
              <S.PublicMatchVersus>vs</S.PublicMatchVersus>
              <S.PublicMatchPlayer>
                <Avatar size="36px" src={match.player_b.avatar} />
                <S.PublicMatchPlayerName>{match.player_b.username}</S.PublicMatchPlayerName>
              </S.PublicMatchPlayer>
            </S.PublicMatchPlayers>
            <S.PublicMatchMeta>{createdLabel}</S.PublicMatchMeta>
          </S.MatchHeaderMain>
          <S.MatchIcon path={mdiArrowRight} size="20px" />
        </S.MatchHeader>
        <S.MatchInfo>
          <S.MatchInfoRow>
            <S.MatchInfoLabel>Status</S.MatchInfoLabel>
            <S.Badge badgeStyle={statusBadge.badgeStyle}>{statusBadge.label}</S.Badge>
          </S.MatchInfoRow>
          {finishReason && (
            <S.MatchInfoRow>
              <S.MatchInfoLabel>Finish reason</S.MatchInfoLabel>
              <S.MatchInfoValue>{finishReason}</S.MatchInfoValue>
            </S.MatchInfoRow>
          )}
          {!isActive && match.winner && (
            <S.MatchInfoRow>
              <S.MatchInfoLabel>Winner</S.MatchInfoLabel>
              <S.MatchInfoValue>
                {match.winner === match.player_a.id ? match.player_a.username : match.player_b.username}
              </S.MatchInfoValue>
            </S.MatchInfoRow>
          )}
        </S.MatchInfo>
      </S.MatchCard>
    );
  };

  const renderOutgoingChallenges = () => {
    return (
      <S.ChallengeList>
        {outgoingChallenges.map((challenge) =>
          renderChallengeCard(
            challenge,
            challenge.opponent,
            <Button onClick={() => handleCancelChallenge(challenge.id)} text="Cancel" />,
          ),
        )}
      </S.ChallengeList>
    );
  };

  return (
    <S.Container className={className}>
      <S.Content>
        <S.ProfileHeader>
          <S.ProfileHeaderContent>
            <S.ProfileAvatarWrapper>
              <Avatar size="96px" src={self?.avatar ?? null} />
            </S.ProfileAvatarWrapper>
            <S.ProfileDetails>
              <S.ProfileUsername>{self?.username ?? 'Unknown player'}</S.ProfileUsername>
              <S.ProfileMeta>
                <S.ProfileMetaItem>{rankLabel}</S.ProfileMetaItem>
                <S.ProfileMetaItem>{`Record ${recordLabel}`}</S.ProfileMetaItem>
              </S.ProfileMeta>
            </S.ProfileDetails>
          </S.ProfileHeaderContent>
        </S.ProfileHeader>
        <S.TopRow>
          <S.EloSection>
            <S.SectionTitle>Your ELO</S.SectionTitle>
            {renderEloChart()}
          </S.EloSection>
          <S.Section>
            <S.SectionTitle>Send a challenge</S.SectionTitle>
            <Formik initialValues={initialValues} onSubmit={handleChallengeSubmit} validationSchema={validationSchema}>
              {({
                dirty,
                errors,
                handleSubmit,
                isSubmitting,
                isValid,
                setFieldTouched,
                setFieldValue,
                touched,
                values,
              }) => (
                <S.Form onSubmit={handleSubmit}>
                  <FormField>
                    <S.UserSearchInput
                      errors={errors}
                      label="Search for recipient"
                      name="opponent"
                      onChange={(user) => handleOpponentChange(user, setFieldTouched, setFieldValue)}
                      touched={touched}
                      value={values.opponent}
                    />
                  </FormField>
                  <S.FormRow>
                    <FormField>
                      <S.Input errors={errors} label="Stake (TNB)" name="stakeAmount" touched={touched} type="number" />
                    </FormField>
                    <FormField>
                      <S.Input
                        errors={errors}
                        label="Max spend (TNB)"
                        name="maxSpendAmount"
                        touched={touched}
                        type="number"
                      />
                    </FormField>
                  </S.FormRow>
                  <FormField>
                    <S.Select
                      errors={errors}
                      label="Total time per player"
                      name="timeLimitSeconds"
                      options={timeLimitOptions}
                      touched={touched}
                    />
                  </FormField>
                  <S.SubmitRow>
                    <S.SubmitButton
                      dirty={dirty}
                      isSubmitting={isSubmitting}
                      isValid={isValid}
                      text={isSubmitting ? 'Sending...' : 'Send challenge'}
                      type={ButtonType.submit}
                    />
                  </S.SubmitRow>
                </S.Form>
              )}
            </Formik>
          </S.Section>
        </S.TopRow>

        {incomingChallenges.length > 0 && (
          <S.MatchesSection>
            <S.MatchesSectionTitle>Incoming challenges</S.MatchesSectionTitle>
            {renderIncomingChallenges()}
          </S.MatchesSection>
        )}

        {outgoingChallenges.length > 0 && (
          <S.MatchesSection>
            <S.MatchesSectionTitle>Outgoing challenges</S.MatchesSectionTitle>
            {renderOutgoingChallenges()}
          </S.MatchesSection>
        )}

        {activeMatchesSorted.length > 0 && (
          <S.MatchesSection>
            <S.MatchesSectionTitle>Your matches</S.MatchesSectionTitle>
            {renderYourMatches()}
          </S.MatchesSection>
        )}

        {publicMatchesSorted.length > 0 && (
          <S.MatchesSection>
            <S.MatchesSectionTitle>Public matches</S.MatchesSectionTitle>
            {renderPublicMatches()}
          </S.MatchesSection>
        )}

        {completedMatchesSorted.length > 0 && (
          <S.MatchesSection>
            <S.MatchesSectionTitle>Match history</S.MatchesSectionTitle>
            {renderCompletedMatches()}
          </S.MatchesSection>
        )}
      </S.Content>
    </S.Container>
  );
};

export default ConnectFiveHome;
